import { PushApiFetch } from 'reactApp/api/pushApiFetch';
import { getSwRevision } from 'serviceWorker/build';
import { swDebugLog } from 'serviceWorker/debugLog';
import { DEEPLINK_REPLY_TYPE, DEEPLINK_REQUEST_TYPE } from 'serviceWorker/deeplinkBridge';
import { readDeviceState } from 'serviceWorker/deviceState';
import {
    getSuppressionConfig,
    getSwOmicronConfig,
    getSwOmicronConfigState,
    isPushDedupEnabled,
    setSwOmicronConfig,
} from 'serviceWorker/omicronConfig';
import {
    type OmicronConfigMessage,
    type OmicronConfigStateMessage,
    OMICRON_CONFIG_REQUEST_TYPE,
    OMICRON_CONFIG_TYPE,
} from 'serviceWorker/omicronConfigBridge';
import { clearPendingDeeplink, readPendingDeeplink, setPendingDeeplink } from 'serviceWorker/pendingDeeplink';
import { getEffectiveSwPlatform } from 'serviceWorker/platform';
import { getAppOpenReason } from 'serviceWorker/push/appPresence';
import { claimPushId } from 'serviceWorker/push/showedPushIds';
import { getSkipReason } from 'serviceWorker/push/skipRules';
import { unwrapPayload } from 'serviceWorker/push/unwrapPayload';
import {
    type PushAction,
    type SuppressReason,
    flushPushAnalytics,
    resolvePushEmail,
    resolvePushType,
    sendActivatedRadar,
    sendApiRadar,
    trackPushEvent,
} from 'serviceWorker/pushAnalytics';
import { initSwConsoleApi } from 'serviceWorker/swConsole';
import { type SwTestPushMessage, type SwTestPushReply, SW_TEST_PUSH_TYPE } from 'serviceWorker/swDebugBridge';

// eslint-disable-next-line init-declarations
declare const self: ServiceWorkerGlobalScope;

type PushPayload = Record<string, unknown>;

const log = (...params: unknown[]) => {
    // eslint-disable-next-line no-console
    console.debug(`[firebase-messaging-sw]`, ...params);
};

const swRevision = getSwRevision();

// Платформы в стартовом логе нет намеренно: на уровне модуля читаем только UA, а он на iPadOS 13+
// врёт (маковский UA без "iPad"). Эффективная платформа пишется в снимок `deeplink-skip-check` при
// каждом пуше — там, где она что-то решает, и уже правильная.
log('Revision:', swRevision);

/** Прочитать строковое поле payload; не-строковые значения дают пустую строку. */
const getString = (data: PushPayload, key: string): string => {
    const value = data[key];

    return typeof value === 'string' ? value : '';
};

// Мгновенное обновление Service Worker у пользователей.
self.addEventListener('install', (event) => {
    event.waitUntil(self.skipWaiting());
});

// Отправку раскатной метки держим в `waitUntil`: запрос к аналитике не привязан к жизни
// воркера сам по себе, и браузер вправе снести его вместе с воркером.
self.addEventListener('activate', (event) => {
    sendActivatedRadar();
    event.waitUntil(Promise.all([self.clients.claim(), flushPushAnalytics()]));
});

// Прогреваем кэш Omicron-конфига на старте SW. SW может быть поднят пушем,
// когда ни одной вкладки нет и прислать конфиг по postMessage некому, — тогда единственный
// источник фич это IDB, и чтение должно стартовать до первого события.
getSwOmicronConfig();

// Удерживаемый диплинк живёт в IDB (`serviceWorker/pendingDeeplink`), а не в переменной модуля.
// Причина: страница забирает его не сразу после клика, а когда пользователь вернёт приложение на
// передний план, и к этому моменту инстанс воркера может быть снесён. Хуже того, REQUEST страницы
// идёт через `registration.active.postMessage()` и сам поднимает мёртвый воркер — новым инстансом
// с пустой памятью, из-за чего потеря выглядела бы как штатное «отдавать было нечего».

// Диагностика из консоли самого воркера: app.webPush.status() / clearDedup().
initSwConsoleApi({
    getPendingDeeplink: async () => {
        const pending = await readPendingDeeplink(Date.now());

        return pending.status === 'fresh' ? pending.appUrl : null;
    },
});

// Отдать странице удержанный диплинк и сразу снять рекорд.
//
// Доставка однократная: воркер дублирует диплинк broadcast'ом уже открытым окнам и отдачей по
// REQUEST только что стартовавшей страницы, и без очистки пуш переоткрылся бы дважды. Держать
// рекорд до подтверждения от страницы пробовали (CLOUDWEB-21080): `window.open` переключает на
// нативное приложение, подтверждение обрывается на полпути, и следующий старт PWA в пределах TTL
// открывал бы нативку повторно.
const replyPendingDeeplink = async (event: ExtendableMessageEvent) => {
    const pending = await readPendingDeeplink(Date.now());

    swDebugLog({ event: 'deeplink-request', reason: event.data?.reason, status: pending.status });

    if (pending.status === 'fresh' && event.source) {
        (event.source as Client).postMessage({ type: DEEPLINK_REPLY_TYPE, appUrl: pending.appUrl });
        swDebugLog({ event: 'deeplink-reply-sent', appUrl: pending.appUrl });
    }

    // Снимаем и протухший рекорд: он уже никому не нужен, а место в базе занимает.
    if (pending.status !== 'empty') {
        await clearPendingDeeplink();
    }
};

// Принять снимок Omicron-фич со страницы: в SW их иначе не прочитать (нет window.cloudSettings).
const receiveOmicronConfig = (event: ExtendableMessageEvent, message: OmicronConfigMessage) => {
    if (!message.config) {
        return;
    }

    // waitUntil: запись в IDB не должна оборваться терминацией SW сразу после message.
    event.waitUntil(setSwOmicronConfig(message.config));
    swDebugLog({
        event: 'omicron-config-received',
        pageTs: message.ts,
        debug: message.config.webpushDebug,
        enablePushDedup: message.config.webpushConfig?.enablePushDedup,
    });
};

// Отдать странице состояние кэша конфига: что воркер реально использует + ревизию скрипта.
// Отвечаем в порт MessageChannel из запроса — так страница дожидается именно своего ответа.
const replyOmicronConfigState = (event: ExtendableMessageEvent) => {
    const port = event.ports[0];

    if (!port) {
        return;
    }

    event.waitUntil(
        getSwOmicronConfigState().then(({ config, savedAt }) => {
            const state: OmicronConfigStateMessage = { config, savedAt, swRevision: getSwRevision() };

            port.postMessage(state);
            swDebugLog({ event: 'omicron-config-state-sent', savedAt, hasConfig: !!config });
        })
    );
};

// Прогнать обработку пуша по команде со страницы (app.webPush.testPush). Показ уведомления
// возможен только здесь, а консоль воркера доступна не во всех браузерах.
const runTestPush = (event: ExtendableMessageEvent, message: SwTestPushMessage) => {
    const port = event.ports[0];
    const reply = (state: SwTestPushReply) => port?.postMessage(state);

    swDebugLog({ event: 'test-push', id: getString(message.payload || {}, 'id') });

    event.waitUntil(
        handlePush(message.payload || {}).then(
            () => reply({ ok: true }),
            (error) => reply({ ok: false, error: String(error) })
        )
    );
};

// Единая точка входа postMessage от Page context — разбор по типу сообщения.
self.addEventListener('message', (event) => {
    const data = event.data;

    switch (data?.type) {
        case OMICRON_CONFIG_TYPE:
            receiveOmicronConfig(event, data as OmicronConfigMessage);
            break;

        case OMICRON_CONFIG_REQUEST_TYPE:
            replyOmicronConfigState(event);
            break;

        case SW_TEST_PUSH_TYPE:
            runTestPush(event, data as SwTestPushMessage);
            break;

        case DEEPLINK_REQUEST_TYPE:
            event.waitUntil(replyPendingDeeplink(event));
            break;

        default:
            break;
    }
});

/** Событие пуша в аналитику: все размерности берутся из самого payload. */
const trackPush = (action: PushAction, data: PushPayload, reason?: SuppressReason): Promise<void> => {
    const appUrl = getString(data, 'app_url');
    const webUrl = getString(data, 'web_url');

    return trackPushEvent(action, {
        campaignId: getString(data, 'campaign_id'),
        url: appUrl || webUrl,
        type: resolvePushType(appUrl, webUrl),
        secure: Boolean(data.secure),
        id: getString(data, 'id'),
        email: resolvePushEmail(getString(data, 'email')),
        reason,
    });
};

// Единый нативный push-listener. Формат payload определяется структурной экстракцией (unwrapPayload); источник пуша значения не имеет.
self.addEventListener('push', (event) => {
    if (!event.data) {
        return;
    }

    // eslint-disable-next-line init-declarations
    let pushData;
    try {
        // Декодируем тело один раз и парсим из него же (json() внутри делает то же самое).
        // Сырой payload попутно уходит в debug-лог: и чтение, и парсинг под одним try — иначе
        // исключение на битом payload убило бы показ пуша целиком.
        const raw = event.data.text();

        swDebugLog({ event: 'push-raw', raw });
        pushData = unwrapPayload(JSON.parse(raw));
    } catch (error) {
        log('Failed to parse push payload', error);
        swDebugLog({ event: 'push-parse-error', error: String(error) });
        return;
    }

    if (!pushData) {
        return;
    }

    if (typeof pushData !== 'object') {
        return;
    }

    event.waitUntil(handlePush(pushData));
});

/**
 * Этот пуш уже показывали?
 */
async function isDuplicatePush(pushId: string): Promise<boolean> {
    const dedupEnabled = await isPushDedupEnabled();

    if (!pushId || !dedupEnabled) {
        // Видно, почему клейма не было: пуш без id или выключенный в Omicron дедуп.
        swDebugLog({ event: 'dedup-skipped', reason: pushId ? 'disabled-by-config' : 'no-id' });

        return false;
    }

    return !(await claimPushId(pushId));
}

async function handlePush(pushData: PushPayload) {
    log('Received background message:', pushData);
    swDebugLog({
        event: 'push-received',
        id: getString(pushData, 'id'),
        secure: Boolean(pushData.secure),
        hasAppUrl: Boolean(getString(pushData, 'app_url')),
        hasWebUrl: Boolean(getString(pushData, 'web_url')),
        campaign_id: getString(pushData, 'campaign_id'),
    });

    const clients = (await self.clients.matchAll({ type: 'window', includeUncontrolled: true })) as WindowClient[];
    const [suppression, deviceState] = await Promise.all([getSuppressionConfig(), readDeviceState()]);
    const now = Date.now();

    const appOpenReason = getAppOpenReason({
        clients,
        visibleAt: deviceState?.visibleAt,
        now,
        enableOpenAppSuppress: suppression.enableOpenAppSuppress,
        presenceTtlMs: suppression.presenceTtlMs,
    });

    // Снимок уезжает независимо от исхода: по нему видно, что iOS реально отдаёт в matchAll, и
    // подтверждается ли гипотеза «клиентов нет либо focused всегда false». Без этих чисел решать,
    // нужен ли маяк вообще, не на чем.
    swDebugLog({
        event: 'app-presence',
        reason: appOpenReason,
        clientsCount: clients.length,
        anyFocused: clients.some((client) => client.focused),
        anyVisible: clients.some((client) => client.visibilityState === 'visible'),
        beaconAge: typeof deviceState?.visibleAt === 'number' ? now - deviceState.visibleAt : null,
        enableOpenAppSuppress: suppression.enableOpenAppSuppress,
    });

    if (appOpenReason) {
        log('App is open — suppressing background notification.', { reason: appOpenReason });
        swDebugLog({ event: 'push-suppressed', reason: appOpenReason, id: getString(pushData, 'id') });
        // Эта ветка раньше не слала вообще ничего, поэтому масштаб бага по проду был неизвестен.
        await trackPush('suppressed', pushData, appOpenReason);
        await flushPushAnalytics();
        return;
    }

    let fetchData: PushPayload | null = null;

    if (pushData.secure && pushData.message_id) {
        try {
            sendApiRadar('req');
            log('Secure push — fetching full payload from API.', { message_id: pushData.message_id });
            const pushApiFetch = new PushApiFetch(getString(pushData, 'email'));

            fetchData = await pushApiFetch.makeRequest('/api/v3/push/message', {
                message_id: pushData.message_id,
                email: pushData.email,
            });

            if (!fetchData) {
                throw new Error('EmptyResponse');
            }

            sendApiRadar('resp');
        } catch (error) {
            log('API Error', error);
            sendApiRadar('err', error);
            await flushPushAnalytics();
            return;
        }
    }

    const data: PushPayload = {
        ...pushData,
        ...(fetchData || {}),
    };

    const appUrl = getString(data, 'app_url');
    const webUrl = getString(data, 'web_url');

    // Из снимка конфига, а не из UA: Safari на iPadOS отдаёт маковский UA, и по нему iPad
    // попадал бы в десктопное правило, где диплинки гасятся целиком.
    const platform = await getEffectiveSwPlatform();

    const skipReason = getSkipReason({
        appUrl,
        webUrl,
        platform,
        deeplinkTarget: suppression.deeplinkTarget,
        skipDeeplinkPushOnDesktop: suppression.skipDeeplinkPushOnDesktop,
        skipDeeplinkPushWithoutTarget: suppression.skipDeeplinkPushWithoutTarget,
    });

    // Снимок входов пишем при любом исходе, как и у проверки «приложение открыто». Иначе на пути
    // «показываем» правило молчит, и по логу не отличить «пуш не диплинковый» от «признак таргета
    // доехал не тем значением» — а это разные диагнозы с разными починками.
    swDebugLog({
        event: 'deeplink-skip-check',
        reason: skipReason,
        hasAppUrl: Boolean(appUrl),
        hasWebUrl: Boolean(webUrl),
        platform,
        deeplinkTarget: suppression.deeplinkTarget,
        skipDeeplinkPushOnDesktop: suppression.skipDeeplinkPushOnDesktop,
        skipDeeplinkPushWithoutTarget: suppression.skipDeeplinkPushWithoutTarget,
    });

    if (skipReason) {
        log('Deeplink push has nowhere to lead — skipping.', { reason: skipReason });
        swDebugLog({ event: 'push-suppressed', reason: skipReason, id: getString(data, 'id') });
        await trackPush('suppressed', data, skipReason);
        await flushPushAnalytics();
        return;
    }

    const pushId = getString(pushData, 'id');

    if (await isDuplicatePush(pushId)) {
        log('Duplicate push — already shown, skipping.', { pushId });
        swDebugLog({ event: 'push-dedup', id: pushId });
        await trackPush('dedup', { ...data, id: pushId });
        await flushPushAnalytics();
        return;
    }

    try {
        const { ack } = JSON.parse(getString(data, 'hub_link') || '{}') as { ack?: unknown };

        if (typeof ack === 'string') {
            // отправляем событие о показе уведомления
            await fetch(ack.replace('http:', 'https:'), { mode: 'no-cors' });
        }
    } catch (_) {}

    const notificationTitle = getString(data, 'ru-title') || getString(data, 'title') || 'Облако Mail';

    const notificationOptions: NotificationOptions = {
        body: getString(data, 'ru-text') || getString(data, 'message_text') || getString(data, 'body') || notificationTitle,
        icon: getString(data, 'icon_url') || '/img/favicon-512.png',
        image: getString(data, 'image_url') || undefined,
        data,
    };

    log('Showing notification.', {
        title: notificationTitle,
        hasBody: Boolean(notificationOptions.body),
        hasAppUrl: Boolean(appUrl),
        hasWebUrl: Boolean(webUrl),
    });

    await self.registration.showNotification(notificationTitle, notificationOptions);
    swDebugLog({ event: 'push-shown', id: getString(data, 'id'), campaign_id: getString(data, 'campaign_id') });

    await trackPush('delivered', data);

    await flushPushAnalytics();
    log('Notification shown, delivered tracked.');
}

self.addEventListener('notificationclick', (event) => {
    event.waitUntil(
        (async () => {
            const data = (event.notification.data ?? {}) as PushPayload;

            // два типа пушей по полям payload:
            //   web_url  — веблинковый пуш: web-URL для openWindow. Если URL same-origin
            //              (в scope SW), openWindow навигирует внутри PWA, не поднимая браузер
            //              поверх него; если cross-origin — открывается внешний браузер.
            //   app_url  — диплинковый пуш: https-URL, который страница превратит в cloudmailru://
            //              и откроет нативное приложение (через postMessage-мост).
            let webUrl = getString(data, 'web_url') || self.location.origin;
            const appUrl = getString(data, 'app_url');
            log('Notification click:', data);

            // Диплинк сохраняем ПЕРВЫМ действием, до всякой сети.
            //
            // На iOS тап по уведомлению немедленно переводит PWA на передний план, и воркер в этот
            // момент могут заморозить. Раньше запись стояла после `fetch(hub_link.open)` и
            // отправки события `opened` — двух сетевых ожиданий, — и на тёплом PWA не доезжала:
            // страница возвращалась на передний план, спрашивала диплинк и получала пустой рекорд.
            // Проверено на стенде: в логе ни `notificationclick`, ни `deeplink-pending-set`, а все
            // запросы страницы отвечают `status: empty`.
            //
            // Порядок теперь такой: сначала то, что видит пользователь, потом телеметрия.
            if (appUrl) {
                await setPendingDeeplink(appUrl);
                // Ждём запись маркера: `swDebugLog` fire-and-forget, а здесь она нужна как
                // доказательство, что событие вообще пришло в воркер.
                await swDebugLog({ event: 'deeplink-pending-set', appUrl });
                // iOS PWA standalone не навигирует PWA на URL из openWindow, поэтому диплинк
                // уходит странице сообщением: broadcast уже открытому окну плюс отдача по REQUEST,
                // который страница шлёт и при возврате на передний план.
                const windows = await self.clients.matchAll({ includeUncontrolled: true, type: 'window' });

                windows.forEach((client) => client.postMessage({ type: DEEPLINK_REPLY_TYPE, appUrl }));
                swDebugLog({ event: 'deeplink-broadcast', appUrl, clients: windows.length });
            } else {
                swDebugLog({ event: 'deeplink-skipped', reason: 'no app_url' });
            }

            // Тоже ждём: без этой записи нельзя отличить «событие не пришло в воркер» от
            // «пришло, но воркер заморозили раньше, чем лог долетел до диска».
            await swDebugLog({
                event: 'notificationclick',
                web_url: data.web_url,
                app_url: appUrl,
                email: data.email,
                campaign_id: data.campaign_id,
                tag: data.tag,
                action: data.action,
            });

            try {
                const { open } = JSON.parse(getString(data, 'hub_link') || '{}') as { open?: unknown };

                if (typeof open === 'string') {
                    // отправляем событие об открытии уведомления
                    await fetch(open.replace('http:', 'https:'), { mode: 'no-cors' });
                }
            } catch (_) {}

            await trackPush('opened', data);

            const email = getString(data, 'email');

            if (email && email !== 'any' && !webUrl.includes('x-email=')) {
                const delimiter = webUrl.includes('?') ? '&' : '?';
                webUrl = `${webUrl}${delimiter}x-email=${encodeURIComponent(email)}`;
            }

            try {
                log('[notificationclick] openWindow webUrl:', webUrl);
                await self.clients.openWindow(webUrl);
                swDebugLog({ event: 'openWindow-resolved', webUrl });
            } catch (err) {
                swDebugLog({ event: 'openWindow-error', webUrl, error: String(err) });
                throw err;
            } finally {
                event.notification.close();
                await flushPushAnalytics();
            }
        })()
    );
});

/**
 * На iOS это событие не приходит
 */
self.addEventListener('notificationclose', (event) => {
    event.waitUntil(
        (async () => {
            const data = (event.notification.data ?? {}) as PushPayload;

            log('Notification close:', data);

            swDebugLog({
                event: 'notificationclose',
                id: getString(data, 'id'),
                web_url: data.web_url,
                app_url: data.app_url,
                campaign_id: data.campaign_id,
            });

            await trackPush('closed', data);

            await flushPushAnalytics();
        })()
    );
});
